import { useTranslation } from 'react-i18next';
import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { usePlatformServices } from '@/platform/context';
import { LiveResource, type Watch } from './resource';

export interface RefreshState { fast: number; slow: number; day: string; now: number }
export function localDay(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}
const initial: RefreshState = { fast: 0, slow: 0, day: localDay(), now: Date.now() };
const fallback = { ...initial, issues: () => [] as string[], refresh: () => {}, resource: <T,>(_key: string, _watch: Watch<T>): LiveResource<T> | null => null };
const Context = createContext(fallback);
export const DATA_CHANGED = 'happily:data-changed';
export function notifyDataChanged() {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(DATA_CHANGED));
}

/**
 * Coming back to the window after less than this is a glance (the tray popover is opened
 * many times an hour): live listeners are already current and the 30-second refresh is
 * due anyway, so it does not re-read everything. Longer away, waking from sleep, coming
 * back online and the connection returning always do.
 */
export const RECOVER_AFTER_AWAY_MS = 60_000;

export function AppDataProvider({ children, scope, watchConnection }: {
  children: React.ReactNode;
  scope: string;
  /**
   * Calls back each time the realtime connection comes back after a drop. Waking the
   * laptop refreshes straight away, usually before the network is up, so those reads
   * fail or come only from the cache; refreshing again once connected corrects them.
   */
  watchConnection?: (onReconnect: () => void) => () => void;
}) {
  const platform = usePlatformServices();
  const [state, setState] = useState(() => ({ ...initial, day: localDay(), now: Date.now() }));
  const resources = useMemo(() => new Map<string, LiveResource<any>>(), [scope]);
  const refresh = React.useCallback(() => {
    setState((s) => ({ fast: s.fast + 1, slow: s.slow + 1, day: localDay(), now: Date.now() }));
    for (const r of resources.values()) if (r.getSnapshot().error) r.restart();
  }, [resources]);
  useEffect(() => {
    let active = true;
    let inactiveSince: number | null = null;
    let lastFast = Date.now();
    let lastSlow = Date.now();
    let lastRecovery = 0;
    let alive = true;
    const recover = () => {
      if (!alive || Date.now() - lastRecovery < 500) return;
      lastRecovery = lastFast = lastSlow = Date.now();
      refresh();
      for (const r of resources.values()) r.restart();
    };
    // A return to the window: a full recovery only after a real absence; a glance only
    // retries what is failing (refresh restarts errored resources).
    const returned = () => {
      const away = inactiveSince === null ? 0 : Date.now() - inactiveSince;
      inactiveSince = null;
      if (away >= RECOVER_AFTER_AWAY_MS) recover();
      else if ([...resources.values()].some((r) => r.getSnapshot().error)) refresh();
    };
    const stopActivity = platform.window.onActivityChanged((next) => {
      if (next && !active) returned();
      if (!next && active) inactiveSince = Date.now();
      active = next;
    });
    // Started hidden (launched at login into the tray): the first open counts as a return
    // from however long it stayed hidden.
    void platform.window.getActivityState().then((value) => {
      if (!alive) return;
      if (!value && active) inactiveSince = Date.now();
      active = value;
    });
    const stopResume = platform.window.onResume?.(recover);
    const timer = window.setInterval(() => {
      const now = Date.now();
      const fastDue = active && now - lastFast >= 30_000;
      const slowDue = active && now - lastSlow >= 300_000;
      if (fastDue) lastFast = now;
      if (slowDue) lastSlow = now;
      setState((s) => !fastDue && !slowDue && s.day === localDay() ? s : ({ fast: s.fast + Number(fastDue), slow: s.slow + Number(slowDue), day: localDay(), now }));
    }, 1000);
    const focused = () => { if (inactiveSince !== null) returned(); };
    const stopConnection = watchConnection?.(recover);
    window.addEventListener('online', recover);
    window.addEventListener('focus', focused);
    window.addEventListener(DATA_CHANGED, refresh);
    const storageChanged = (event: StorageEvent) => { if (!event.key || !event.key.includes('assistant')) recover(); };
    window.addEventListener('storage', storageChanged);
    return () => {
      alive = false;
      window.clearInterval(timer);
      stopActivity(); stopResume?.(); stopConnection?.();
      window.removeEventListener('online', recover);
      window.removeEventListener('focus', focused);
      window.removeEventListener(DATA_CHANGED, refresh);
      window.removeEventListener('storage', storageChanged);
      // StrictMode replays effects: resources are restarted by subscribers after cleanup.
    };
  }, [platform, refresh, watchConnection]);
  const value = useMemo(() => ({ ...state, refresh, issues: () => [...resources.values()].filter((r) => r.isActive() && r.getSnapshot().error).map((r) => r.getSnapshot().error!), resource: <T,>(key: string, watch: Watch<T>) => {
    let r = resources.get(key);
    if (!r) { r = new LiveResource(watch, () => setState((s) => ({ ...s })), () => {
      // Drop unused scope/page resources without breaking StrictMode's immediate resubscribe.
      setTimeout(() => {
        if (resources.get(key) === r && !r!.isActive()) { r!.dispose(); resources.delete(key); }
      }, 0);
    }); resources.set(key, r); }
    return r as LiveResource<T>;
  } }), [state, refresh, resources]);
  // Disposal deferred one task so StrictMode's setup/cleanup/setup does not kill live stores.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const registry = resources as Map<string, LiveResource<any>> & { cleanup?: ReturnType<typeof setTimeout> };
    if (registry.cleanup) clearTimeout(registry.cleanup);
    return () => { timer = setTimeout(() => { registry.forEach((r) => r.dispose()); registry.clear(); }, 0); registry.cleanup = timer; };
  }, [resources]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}
export function useRefreshClock() { return useContext(Context); }
const empty = { data: null, confirmed: false, updatedAt: null, error: null };
const noopSubscribe = () => () => {};
/**
 * `keepPrevious`: when the key moves on (a new day, a changed team), keep showing the
 * last server-confirmed data until the new subscription has its own confirmed answer,
 * instead of a blank or a partial cache snapshot. The same rule LiveResource applies
 * within one key, carried across keys for this call site.
 */
export function useLiveResource<T>(key: string, watch: Watch<T>, options?: { keepPrevious?: boolean }) {
  const context = useContext(Context);
  const resource = context.resource<T>(key, watch);
  const state = useSyncExternalStore(resource?.subscribe ?? noopSubscribe, resource?.getSnapshot ?? (() => empty));
  const lastConfirmed = useRef<T | null>(null);
  if (state.confirmed && state.data !== null) lastConfirmed.current = state.data;
  if (options?.keepPrevious && !state.confirmed && lastConfirmed.current !== null) {
    return { ...state, data: lastConfirmed.current, available: !!resource };
  }
  return { ...state, available: !!resource };
}
export function FreshnessNotice() {
  const clock = useRefreshClock();
  const { t } = useTranslation('layout');
  const [offline, setOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const update = () => setOffline(!navigator.onLine);
    window.addEventListener('online', update); window.addEventListener('offline', update);
    return () => { window.removeEventListener('online', update); window.removeEventListener('offline', update); };
  }, []);
  if (!offline && !clock.issues().length) return null;
  return <div role="status" className="px-3 py-1 text-xs bg-amber-50 text-amber-900">{t(offline ? 'sync.offline' : 'sync.failed')} <button onClick={clock.refresh}>{t('sync.retry')}</button></div>;
}

export function ResourceNotice({ state }: { state: { confirmed: boolean; data: unknown; error: string | null } }) {
  const clock = useRefreshClock();
  const { t } = useTranslation('layout');
  if (!state.error && state.confirmed) return null;
  return <div role="status" className="px-3 py-1 text-xs text-text-muted">{state.error ? t('sync.failed') : state.data ? t('sync.saved') : t('sync.updating')} {state.error && <button onClick={clock.refresh}>{t('sync.retry')}</button>}</div>;
}
